/**
 * Every language the site speaks.
 *
 * `code` is what appears in `?lang=` and in the locale filename, so it is
 * load-bearing and must not change once a locale file exists. Names are given
 * in the language itself — a reader looking for their own language scans for
 * "Français", not "French".
 *
 * `shipped` is the single switch that makes a language public: flip it to
 * `true` once src/i18n/locales/<code>.json exists and passes locales.test.js,
 * and every UI surface (switcher, palette, footer, hreflang, resolution) picks
 * it up automatically. Left `false`, an entry is reserved but not offered —
 * it must be an explicit flag, not filesystem/dynamic-import detection, so
 * webpack can see the shipped set statically and tests can assert on it.
 */

export const LANGUAGES = [
  { code: 'en',      nativeName: 'English',     englishName: 'English',              dir: 'ltr', shipped: true },
  { code: 'fr',      nativeName: 'Français',    englishName: 'French',               dir: 'ltr', shipped: true },
  { code: 'es',      nativeName: 'Español',     englishName: 'Spanish',              dir: 'ltr', shipped: false },
  { code: 'pt',      nativeName: 'Português',   englishName: 'Portuguese',           dir: 'ltr', shipped: false },
  { code: 'de',      nativeName: 'Deutsch',     englishName: 'German',               dir: 'ltr', shipped: false },
  { code: 'nl',      nativeName: 'Nederlands',  englishName: 'Dutch',                dir: 'ltr', shipped: false },
  { code: 'it',      nativeName: 'Italiano',    englishName: 'Italian',              dir: 'ltr', shipped: false },
  { code: 'zh-Hans', nativeName: '中文（简体）', englishName: 'Chinese (Simplified)', dir: 'ltr', shipped: false },
  { code: 'ja',      nativeName: '日本語',       englishName: 'Japanese',             dir: 'ltr', shipped: false },
  { code: 'hi',      nativeName: 'हिन्दी',         englishName: 'Hindi',                dir: 'ltr', shipped: false },
  { code: 'ar',      nativeName: 'العربية',       englishName: 'Arabic',               dir: 'rtl', shipped: false },
  { code: 'af',      nativeName: 'Afrikaans',   englishName: 'Afrikaans',            dir: 'ltr', shipped: false },
  { code: 'zu',      nativeName: 'isiZulu',     englishName: 'Zulu',                 dir: 'ltr', shipped: false },
];

export const LANGUAGE_CODES = LANGUAGES.map((language) => language.code);

// The list every user-facing surface (switcher, palette, footer, hreflang,
// resolution) must render from — never LANGUAGES directly, or an untranslated
// language becomes selectable again.
export const SHIPPED_LANGUAGES = LANGUAGES.filter((language) => language.shipped);
export const SHIPPED_LANGUAGE_CODES = SHIPPED_LANGUAGES.map((language) => language.code);

export const DEFAULT_LANGUAGE = 'en';

export function isLanguageCode(value) {
  return typeof value === 'string' && LANGUAGE_CODES.includes(value);
}

export function isShippedLanguageCode(value) {
  return typeof value === 'string' && SHIPPED_LANGUAGE_CODES.includes(value);
}

export function languageByCode(code) {
  return LANGUAGES.find((language) => language.code === code);
}

export function dirFor(code) {
  return languageByCode(code)?.dir || 'ltr';
}
